import { useState, useEffect } from "react";
import { PhoneOutgoing, Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const RANGES = [["1", "Today"], ["7", "7 days"], ["30", "30 days"]];
const since = (days) => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - (Number(days) - 1)); return d.toISOString(); };

const Tile = ({ label, value, testId }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3"><p className="text-[11px] uppercase tracking-wider font-bold text-slate-400">{label}</p><p className="text-2xl font-black text-slate-900" data-testid={testId}>{value}</p></div>
);

export const CallStatsBoard = () => {
  const { user } = useAuth();
  const [days, setDays] = useState("1");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/api/callcenter/stats", { params: { since: since(days) } }).then(r => setData(r.data)).catch(() => setData({ reps: [], totals: { calls: 0, connects: 0, demos: 0, connect_rate: 0 } }));
  }, [days]);
  const t = data?.totals;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mt-4" data-testid="call-stats-board">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div><h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><PhoneOutgoing className="w-5 h-5 text-blue-600" />Call Outcomes</h2>
          <p className="text-xs text-slate-500">Connect = reached a real person (Interested, Call back, Not interested, Booked demo, Do not call)</p></div>
        <div className="flex bg-slate-100 rounded-lg p-1" data-testid="call-stats-range">
          {RANGES.map(([k, label]) => <button key={k} onClick={() => setDays(k)} className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${days === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid={`call-stats-range-${k}`}>{label}</button>)}
        </div>
      </div>
      {!data ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Tile label="Calls" value={t.calls} testId="call-stats-total-calls" /><Tile label="Connects" value={t.connects} testId="call-stats-total-connects" />
          <Tile label="Connect rate" value={`${t.connect_rate}%`} testId="call-stats-total-rate" /><Tile label="Demos booked" value={t.demos} testId="call-stats-total-demos" />
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead className="w-8 px-2">#</TableHead><TableHead>Rep</TableHead><TableHead className="text-right">Calls</TableHead><TableHead className="text-right">Connects</TableHead><TableHead className="text-right hidden sm:table-cell">Rate</TableHead><TableHead className="text-right">Demos</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.reps.map((r, i) => (
                <TableRow key={r.rep_id} className={r.rep_id === (user?._id || user?.id) ? "bg-blue-50/60" : ""} data-testid={`call-stats-row-${r.rep_id}`}>
                  <TableCell className="font-bold text-slate-500 px-2">{i + 1}</TableCell>
                  <TableCell className="font-semibold text-slate-900">{r.name}{r.rep_id === (user?._id || user?.id) && <span className="ml-2 text-[10px] font-bold text-blue-600 uppercase">You</span>}</TableCell>
                  <TableCell className="text-right" data-testid={`call-stats-calls-${r.rep_id}`}>{r.calls}</TableCell>
                  <TableCell className="text-right" data-testid={`call-stats-connects-${r.rep_id}`}>{r.connects}</TableCell>
                  <TableCell className="text-right text-slate-500 hidden sm:table-cell">{r.connect_rate}%</TableCell>
                  <TableCell className="text-right font-black text-green-700" data-testid={`call-stats-demos-${r.rep_id}`}>{r.demos}</TableCell>
                </TableRow>
              ))}
              {data.reps.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8" data-testid="call-stats-empty">No reps yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </>)}
    </div>
  );
};
